// Portal: Site Health dashboard. The customer-facing cousin of the
// internal CHI Dashboard -- same look (bucket chips that filter, a
// worst-first matrix) but scored ONLY on operational signals the
// customer can act on:
//
//   Shift data delivery         (weight 30)  -- is the POS reporting
//   Back-office login recency   (weight 25)  -- is staff using it
//   Price book update recency   (weight 25)  -- is the book maintained
//   Fuel reconciliation recency (weight 20)  -- monthly cadence scale
//
// Deliberately excludes the internal CHI/SHI (renewal + retention
// signals are XSite's churn lens, not the customer's). Computed
// client-side from /api/portal/sites -- no new API surface.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y || !m || !d) return '—';
  return `${MONTHS_SHORT[Number(m) - 1] || m} ${d}, ${y}`;
}

function daysSince(value) {
  if (!value) return null;
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 86400000;
}

// Bucketed sub-scores. Login + price book on a weekly scale, fuel
// recon on its monthly cadence (mirrors the Sites-tab dot thresholds).
function weeklySignal(days) {
  if (days === null) return 0;
  if (days <= 7) return 100;
  if (days <= 14) return 60;
  if (days <= 30) return 30;
  return 0;
}
function monthlySignal(days) {
  if (days === null) return 0;
  if (days <= 35) return 100;
  if (days <= 60) return 60;
  if (days <= 90) return 30;
  return 0;
}
// Shift files flow continuously from the POS -- tight scale.
function shiftSignal(days) {
  if (days === null) return 0;
  if (days <= 2) return 100;
  if (days <= 5) return 60;
  if (days <= 10) return 30;
  return 0;
}

function scoreSite(s) {
  const shift = shiftSignal(daysSince(s.xsiteio_last_shift_at));
  const login = weeklySignal(daysSince(s.xsiteio_last_login));
  const book  = weeklySignal(daysSince(s.xsiteio_last_updated));
  const recon = monthlySignal(daysSince(s.xsiteio_last_fuel_recon_at));
  // If the shift probe hasn't run for this site yet (timestamp null),
  // fall back to the original 3-signal weighting rather than tanking
  // the score on missing data.
  if (s.xsiteio_last_shift_at == null) {
    const score = Math.round((login * 35 + book * 35 + recon * 30) / 100);
    return { score, shift: null, login, book, recon };
  }
  const score = Math.round((shift * 30 + login * 25 + book * 25 + recon * 20) / 100);
  return { score, shift, login, book, recon };
}

// Same buckets as the internal CHI dashboard: >=80 green, >=50 amber.
function bucketFor(score) {
  if (score >= 80) return 'good';
  if (score >= 50) return 'warn';
  return 'bad';
}
const BUCKET_LABELS = { good: 'Healthy', warn: 'Needs attention', bad: 'At risk' };

function SignalDot({ value }) {
  const tone = value >= 100 ? 'good' : value >= 60 ? 'warn' : value >= 30 ? 'warn' : 'bad';
  return <span className={`portal-dot portal-dot-${tone}`} aria-hidden="true" />;
}

export default function PortalHealth() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [bucket, setBucket] = useState(null); // null = all

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/portal/sites')
      .then((res) => { if (!cancelled) setRows(res.data); })
      .catch(() => { if (!cancelled) setError('Failed to load site health. Please try again.'); });
    return () => { cancelled = true; };
  }, []);

  // Grace period: sites still onboarding, or installed in the last 45
  // days, are ramping up -- scoring them red would just be noise.
  const isNew = (s) => {
    if (s.onboarding_status && s.onboarding_status !== 'Done') return true;
    if (s.install_date) {
      const days = (Date.now() - new Date(s.install_date).getTime()) / 86400000;
      if (days < 45) return true;
    }
    return false;
  };

  const scored = useMemo(() => {
    if (!rows) return [];
    return rows
      .filter((s) => s.status === 'Active' && !isNew(s))
      .map((s) => {
        const sc = scoreSite(s);
        return { ...s, ...sc, bucket: bucketFor(sc.score) };
      })
      .sort((a, b) => a.score - b.score); // worst first -- that's the point
  }, [rows]);

  const newCount = useMemo(
    () => (rows ? rows.filter((s) => s.status === 'Active' && isNew(s)).length : 0),
    [rows]
  );

  const counts = useMemo(() => {
    const c = { good: 0, warn: 0, bad: 0 };
    scored.forEach((s) => { c[s.bucket]++; });
    return c;
  }, [scored]);

  const visible = bucket ? scored.filter((s) => s.bucket === bucket) : scored;

  return (
    <section className="page">
      <div className="page-header">
        <h2>Site Health</h2>
        {rows && <span className="portal-count">{scored.length} active sites</span>}
      </div>

      {error && <p className="portal-error">{error}</p>}
      {!rows && !error && <p className="placeholder-note">Loading…</p>}

      {rows && (
        <>
          <div className="portal-health-chips">
            {['good', 'warn', 'bad'].map((b) => (
              <button
                key={b}
                type="button"
                className={`portal-health-chip is-${b}${bucket === b ? ' is-active' : ''}`}
                onClick={() => setBucket(bucket === b ? null : b)}
              >
                <span className="portal-health-chip-count">{counts[b]}</span>
                <span className="portal-health-chip-label">{BUCKET_LABELS[b]}</span>
              </button>
            ))}
          </div>

          <div className="table-wrap">
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Score</th>
                  <th>Shift Data</th>
                  <th>Back-office Login</th>
                  <th>Price Book Updates</th>
                  <th>Fuel Reconciliation</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div>{s.site_name}</div>
                      <div className="mono" style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-small)' }}>
                        {s.site_number} · {[s.city, s.province_state].filter(Boolean).join(', ')}
                      </div>
                    </td>
                    <td>
                      <div className="portal-score-wrap">
                        <span className={`portal-score mono is-${s.bucket}`}>{s.score}</span>
                        <span className="portal-score-bar">
                          <span
                            className={`portal-score-fill is-${s.bucket}`}
                            style={{ width: `${s.score}%` }}
                          />
                        </span>
                      </div>
                    </td>
                    <td className="mono">
                      {s.shift === null
                        ? <span style={{ color: 'var(--color-text-faint)' }}>—</span>
                        : <><SignalDot value={s.shift} /> {fmtDate(s.xsiteio_last_shift_at)}</>}
                    </td>
                    <td className="mono"><SignalDot value={s.login} /> {fmtDate(s.xsiteio_last_login)}</td>
                    <td className="mono"><SignalDot value={s.book} /> {fmtDate(s.xsiteio_last_updated)}</td>
                    <td className="mono"><SignalDot value={s.recon} /> {fmtDate(s.xsiteio_last_fuel_recon_at)}</td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={6} className="placeholder-note">No sites in this bucket.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <p className="portal-footnote">
            Score blends how recently each site delivered shift data from its
            POS, logged into the back office, updated its price book, and
            completed a fuel reconciliation (reconciliations are scored on a
            monthly cadence). A red Shift Data signal usually means a
            data-feed issue rather than anything your staff did — contact us. Click a bucket
            to filter. Sites are listed worst-first so the ones needing a
            nudge are at the top.
            {newCount > 0 && (
              <> {newCount} new site{newCount === 1 ? ' is' : 's are'} still
              ramping up and not scored yet — see the{' '}
              <a href="/portal/onboarding">Onboarding</a> tab.</>
            )}
          </p>
        </>
      )}
    </section>
  );
}
